import {useEffect, useRef, useCallback} from 'react';
import {fetchStatus} from '../api/API';

/**
 * Custom hook for polling task status with flexible error handling
 *
 * @param {string} token - Auth token
 * @param {Object} callbacks - Callback functions for different states
 *   - onError(status, statusObject): Called on error/verification_failed/parent_failed
 *   - onSuccess(statusObject): Called when status === "completed"
 *   - onPollingError(error): Called on fetch/network errors
 *   - onAuthError(): Called when 401 is received during polling
 * @param {number} interval - Polling interval in ms (default 5000)
 * @returns {Object} - { startPolling, stopPolling, isPolling }
 */
export function useTaskPolling(token, callbacks, interval = 5000) {
    const intervalRef = useRef(null);
    const isPollingRef = useRef(false);
    const callbacksRef = useRef(callbacks);

    // Update callbacks ref when they change
    useEffect(() => {
        callbacksRef.current = callbacks;
    }, [callbacks]);

    const stopPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            isPollingRef.current = false;
        }
    }, []);

    const startPolling = useCallback((taskId) => {
        if (!taskId || !token) return;

        // Clear any existing interval
        stopPolling();

        isPollingRef.current = true;

        intervalRef.current = setInterval(async () => {
            try {
                const status = await fetchStatus(token, taskId);

                // Check for auth error (401)
                if (status.authError) {
                    console.error('[useTaskPolling] Auth error during polling - session may have expired');
                    stopPolling();
                    if (callbacksRef.current.onAuthError) {
                        callbacksRef.current.onAuthError();
                    }
                    return;
                }

                // Check for other HTTP errors
                if (status.error && status.httpStatus) {
                    console.error('[useTaskPolling] HTTP error:', status.httpStatus);
                    stopPolling();
                    if (callbacksRef.current.onPollingError) {
                        callbacksRef.current.onPollingError(new Error(`HTTP ${status.httpStatus}`));
                    }
                    return;
                }

                // Check error states
                if (status.status === "error" ||
                    status.status === "verification_failed" ||
                    status.status === "parent_failed") {
                    stopPolling();
                    if (callbacksRef.current.onError) {
                        callbacksRef.current.onError(status.status, status);
                    }
                    return;
                }

                // Check completion
                if (status.status === "completed" && status.result?.output) {
                    stopPolling();
                    if (callbacksRef.current.onSuccess) {
                        callbacksRef.current.onSuccess(status);
                    }
                }
            } catch (err) {
                console.error("Error checking status:", err);
                stopPolling();
                if (callbacksRef.current.onPollingError) {
                    callbacksRef.current.onPollingError(err);
                }
            }
        }, interval);
    }, [token, interval, stopPolling]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]);

    return {
        startPolling,
        stopPolling,
        isPolling: isPollingRef.current
    };
}
